import { useWeb3React } from '@web3-react/core';
import { formatEther, formatUnits, parseEther, parseUnits } from 'ethers/lib/utils';
import { useState, useMemo, ReactNode, useReducer } from 'react';
import AssetInput from '../../../../../components/AssetInput';
import PercentageSelector from '../../../../../components/PercentageSelector';
import { config } from '../../../../../config/config';
import {
    OrderBookType,
    Tokens,
    INSUFFICIENT_BALANCE,
    Validators,
    OrderDataTypes,
    EnabledChains,
} from '../../../../../config/constants';
import { RemoveMarginFromPositionParamsStruct } from '../../../../../config/contracts/typechain/ITradeManager';
import useModalState from '../../../../../context/modal/useModalState';
import { formatPercentages, formatUSD } from '../../../../../helpers';
import { UserPositionRaw } from '../../../../../hooks/web3/trade/useUserPositions';
import useTokenDetails from '../../../../../hooks/web3/useTokenDetails';
import { preventOverflow, toBigNumber } from '../../../../../web3/utils';
import LimitPriceDropdown from '../../LimitPriceDropdown';
import WithdrawPreview from './WithdrawPreview';
import calculateBankruptcyPrice from '../../../../../helpers/calculations/calculateBankruptcyPrice';
import { UserOrder } from '../../../../../hooks/web3/trade/useUserOpenOrders';
import useCancelOrder from '../../../../../hooks/web3/trade/useCancelOrder';
import { EditPositionType } from '..';
import useCreateOrder from '../../../../../hooks/web3/trade/useCreateOrder';
import useMinCollateral from '../../../../../hooks/web3/trade/useMinCollateral';

export type EditPositionWithdrawProps = {
    children(confirm: {
        confirm: () => Promise<void>;
        validators: Validators[];
    }): ReactNode;
    setOrderCancelled: React.Dispatch<React.SetStateAction<boolean>>;
    orderCancelled: boolean;
};

const EditPositionWithdraw: React.FC<EditPositionWithdrawProps> = ({
    children,
    setOrderCancelled,
    orderCancelled,
}) => {
    const { chainId } = useWeb3React();
    const { modalData } = useModalState();

    const userPosition: UserPositionRaw | UserOrder | undefined = modalData.data.position;
    const userOrder =
        userPosition &&
        'orderData' in userPosition &&
        modalData.data.defaultEditTab === EditPositionType.WITHDRAW
            ? userPosition
            : undefined;

    const { decimals } = useTokenDetails(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );
    const cancel = useCancelOrder();
    const createOrder = useCreateOrder();

    const { minCollateral } = useMinCollateral(userPosition?.tradePair.address);

    const [amount, setAmount] = useState(
        userOrder?.orderData
            ? formatUnits(
                  (
                      userOrder.orderData as OrderDataTypes<RemoveMarginFromPositionParamsStruct>
                  ).params.removedMargin.toString(),
                  decimals
              )
            : ''
    );
    const [isLimitOrder, toggleIsLimitOrder] = useReducer(
        (prev) => !prev,
        !!userOrder?.limitPrice
    );
    const [limitPrice, setLimitPrice] = useState(userOrder?.limitPrice || '');
    const [withExpiryDate, toggleWithExpiryDate] = useReducer(
        (prev) => !prev,
        !!userOrder?.orderData.constraints.deadline
    );
    const [expiryDate, setExpiryDate] = useState<Date | undefined>(
        userOrder
            ? new Date(Number(userOrder.orderData.constraints.deadline) * 1000)
            : undefined
    );

    const isAboveMaxLeverage = parseUnits(userPosition?.leverage || '0', decimals).gt(
        parseUnits(userPosition?.tradePair.maxLeverage || '0', decimals)
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        setAmount(preventOverflow(value, decimals));
    };

    const handleMax = () => {
        if (isAboveMaxLeverage) return;

        setAmount(
            formatUSD(formatUnits(maxWithdrawalAmount || '0', decimals) || '0', {
                useGrouping: false,
                currency: '',
                maxDecimals: decimals,
            })
        );
    };

    const getBankruptcyPrice = () => {
        const newCollateral = parseEther(userPosition?.margin || '0').sub(
            parseEther(amount || '0')
        );

        const newLeverage = parseEther(userPosition?.volume || '0')
            .mul(parseEther('1'))
            .div(newCollateral);

        return calculateBankruptcyPrice(
            userPosition?.entryPrice,
            userPosition?.isShort,
            formatEther(newLeverage)
        );
    };

    const confirm = async () => {
        if (!userPosition) throw new Error('UserPosition is undefined');

        if (userOrder && !orderCancelled) {
            await cancel(userOrder.orderId, userOrder.orderType, () =>
                setOrderCancelled(true)
            );
            return;
        }

        await createOrder(
            OrderBookType.REMOVE_MARGIN,
            {
                positionId: userPosition.id,
                tradePair: userPosition.tradePair.address,
                removedMargin: parseUnits(amount, decimals).toString(),
            },
            'RemoveMarginFromPositionOrder',
            userPosition.isShort,
            getBankruptcyPrice,
            {
                expiryDate: withExpiryDate ? expiryDate : undefined,
                limitPrice: isLimitOrder ? limitPrice : undefined,
                isMarketOrder: !limitPrice ? true : undefined,
                tradePair: userPosition.tradePair,
                userPosition,
            }
        );
    };

    const maxWithdrawalCollateral = useMemo(
        () =>
            toBigNumber(userPosition?.margin || '0', decimals).sub(
                toBigNumber(userPosition?.volume || '0', decimals)
                    .mul(toBigNumber('1', decimals))
                    .div(
                        toBigNumber(userPosition?.tradePair.maxLeverage || '1', decimals)
                    )
            ),
        [
            userPosition?.margin,
            userPosition?.volume,
            userPosition?.tradePair.maxLeverage,
            decimals,
        ]
    );

    const maxWithdrawalAmount = useMemo(() => {
        if (
            !minCollateral ||
            toBigNumber(userPosition?.margin || '0', decimals).lte(
                toBigNumber(minCollateral, decimals)
            )
        )
            return toBigNumber('0');

        if (
            toBigNumber(userPosition?.margin || '0', decimals)
                .sub(maxWithdrawalCollateral)
                .gt(toBigNumber(minCollateral, decimals))
        ) {
            return maxWithdrawalCollateral;
        }

        return toBigNumber(userPosition?.margin || '0', decimals).sub(
            toBigNumber(minCollateral, decimals)
        );
    }, [maxWithdrawalCollateral, userPosition?.margin, minCollateral, decimals]);

    const validators: Validators[] = [
        {
            errorCondition: isAboveMaxLeverage,
            message: `Max leverage is ${formatPercentages(
                userPosition?.tradePair.maxLeverage || 0,
                0
            )}x`,
        },
        {
            errorCondition: !amount || parseEther(amount).isZero(),
            message: 'Amount is required!',
        },
        {
            errorCondition: parseUnits(amount || '0', decimals).gt(maxWithdrawalAmount),
            message: INSUFFICIENT_BALANCE,
        },
        {
            errorCondition:
                isLimitOrder && (!limitPrice || parseEther(limitPrice).isZero()),
            message: 'Limit Price is required!',
        },
        {
            errorCondition: withExpiryDate && !expiryDate,
            message: 'Expiry Date is required!',
        },
    ];

    return (
        <>
            <span className='position-type-text'>
                Remove collateral to increase your leverage
            </span>
            <div className='collateral-container'>
                <AssetInput
                    label='Collateral'
                    sideLabel={
                        <>
                            Balance:{' '}
                            {formatUSD(
                                isAboveMaxLeverage
                                    ? '0'
                                    : formatUnits(maxWithdrawalAmount || '0', decimals),
                                {
                                    currency: '',
                                }
                            )}{' '}
                            USDC
                        </>
                    }
                    name='amount'
                    value={amount}
                    onChange={handleChange}
                    asset={Tokens.USDC}
                    onMax={handleMax}
                    disabled={(!!userOrder && !orderCancelled) || isAboveMaxLeverage}
                />{' '}
                <PercentageSelector
                    currentValue={amount}
                    totalValue={formatUnits(maxWithdrawalAmount || '0', decimals)}
                    handleChange={(value) =>
                        (!!userOrder && !orderCancelled) || isAboveMaxLeverage
                            ? undefined
                            : setAmount(preventOverflow(value, decimals))
                    }
                    disableButtons={
                        (!!userOrder && !orderCancelled) || isAboveMaxLeverage
                    }
                    decimals={decimals}
                />
            </div>
            <LimitPriceDropdown
                selectedTradePair={userPosition?.tradePair}
                limitPriceChecked={isLimitOrder}
                toggleLimitPrice={toggleIsLimitOrder}
                orderExpiryChecked={withExpiryDate}
                toggleOrderExpiry={toggleWithExpiryDate}
                amount={limitPrice}
                onChange={(value) => setLimitPrice(value)}
                date={expiryDate}
                handleDateChange={setExpiryDate}
                disabled={!!userOrder && !orderCancelled}
            />
            <WithdrawPreview amount={amount} userPosition={userPosition} />
            {children({ confirm, validators })}
        </>
    );
};

export default EditPositionWithdraw;
